/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Button } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { BaseTentacleEndpointConfiguration } from "@octopusdeploy/octopus-server-client";
import { CommunicationStyleValues } from "@octopusdeploy/octopus-server-client";
import { EndpointRegistrationKeyValues, links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { WindowsPollingTentacleCallout, LinuxPollingTentacleCallout } from "~/areas/infrastructure/components/TentacleCallout";
import { PollingTarget } from "~/components/Images/MachineSettings/PollingTarget";
import { ExpandableFormSection, Summary, Text, Note } from "~/components/form";
import LinuxCateogry from "./LinuxCategoryDefinition";
import { InfoRegistrationCards } from "./RegistrationCards";
import TentacleThumbprint from "./TentacleThumbprint";
import WindowsCategory from "./WindowsCategoryDefinition";
import type { MachineRegistration, CategorizedEndpointRegistration } from "./endpointRegistry";
import { EndpointSelectionScope } from "./endpointRegistry";
import styles from "./styles.module.less";
interface TentacleActiveEndpointProps {
    endpoint: BaseTentacleEndpointConfiguration;
    serverThumbprint: string | null;
    onChange(newValue: BaseTentacleEndpointConfiguration): void;
}
export class TentacleActiveEndpoint extends React.Component<TentacleActiveEndpointProps> {
    render() {
        return (<div>
                <TentacleThumbprint endpoint={this.props.endpoint} serverThumbprint={this.props.serverThumbprint} onChange={(endpoint) => {
                this.props.onChange(endpoint);
            }}/>
                <ExpandableFormSection errorKey="Uri" title="Subscription ID" focusOnExpandAll summary={this.props.endpoint.Uri ? Summary.summary(this.props.endpoint.Uri) : Summary.placeholder("No uri specified")} help="The unique polling subscription id used by the Tentacle.">
                    <Text label="Subscription ID" placeholder="poll://abcdefghijklmnopqrst/" value={this.props.endpoint.Uri} onChange={(x) => {
                const endpoint = this.props.endpoint;
                endpoint.Uri = x;
                this.props.onChange(endpoint);
            }} type="url"/>
                    <Note>The subscription id is a random 20 character id that is used to queue messages from the server to the Polling Tentacle. This should match the value in the Tentacle config file.</Note>
                </ExpandableFormSection>
            </div>);
    }
    static displayName = "TentacleActiveEndpoint";
}
const WindowsTentacleActiveDialogView: React.FC<{}> = () => (<div>
        <WindowsPollingTentacleCallout />
        <h3 style={{ marginTop: space[32] }}>After installation</h3>
        <p>The new Polling Tentacle will automatically show up in the Deployment Targets list.</p>
    </div>);
WindowsTentacleActiveDialogView.displayName = "WindowsTentacleActiveDialogView"
const LinuxTentacleActiveDialogView: React.FC<{}> = () => (<div>
        <LinuxPollingTentacleCallout />
        <h3 style={{ marginTop: space[32] }}>After installation</h3>
        <p>The new Polling Tentacle will automatically show up in the Deployment Targets list.</p>
    </div>);
LinuxTentacleActiveDialogView.displayName = "LinuxTentacleActiveDialogView"
const TentacleAdditionalActions: React.FC<{
    spaceId: string;
    scope: EndpointSelectionScope;
}> = ({ spaceId, scope }) => {
    const deploymentTargetLink = scope === EndpointSelectionScope.DeploymentTarget;
    const workerLink = scope === EndpointSelectionScope.Worker;
    return (<React.Fragment>
            {deploymentTargetLink && (<Button key="deployment-targets" importance="secondary" label="View Deployment Targets" href={links.deploymentTargetsPage.generateUrl({ spaceId }, { deploymentTargetTypes: [CommunicationStyleValues.TentacleActive] })}/>)}
            {workerLink && <Button key="workers" importance="secondary" label="View Workers" href={links.workersPage.generateUrl({ spaceId }, { commStyles: [CommunicationStyleValues.TentacleActive] })}/>}
        </React.Fragment>);
};
TentacleAdditionalActions.displayName = "TentacleAdditionalActions"
export default TentacleActiveEndpoint;
function createLogo(registration: CategorizedEndpointRegistration): React.ReactElement {
    return <PollingTarget className={styles.centreThumbnail} title={registration.name}/>;
}
const activeTentacleEndpointRegistration: MachineRegistration = {
    key: EndpointRegistrationKeyValues.TentacleActive,
    displayOrder: 10,
    categories: [WindowsCategory, LinuxCateogry],
    name: "Polling Tentacle",
    communicationStyle: CommunicationStyleValues.TentacleActive,
    discoverable: false,
    renderCard: InfoRegistrationCards.basic(({ category }) => (category.category === WindowsCategory.category ? <WindowsTentacleActiveDialogView /> : <LinuxTentacleActiveDialogView />), ({ registration }) => ({
        logo: createLogo(registration),
        registrationName: registration.name,
        description: "A Polling target will actively poll the Octopus Server for tasks to perform.",
        communicationStyle: registration.communicationStyle,
    }), ({ spaceId, scope }) => <TentacleAdditionalActions spaceId={spaceId} scope={scope}/>, ({ onClick }) => onClick),
};
export { activeTentacleEndpointRegistration };
